import * as React from 'react';
import onClickOutside from 'react-onclickoutside';
import classnames from 'classnames';

import Checkbox from '@components/Checkbox';
import ClippedText from '@components/ClippedText';
import { DateFilter } from '@components/CustomFilter/DateFilter';
import Input from '@components/Input';

import { getTodaysDate } from '@utils/CalendarUtil';
import { isValidDateFilter } from '@utils/CustomCLPFilterUtil';
import { getBoundingClientRect } from '@utils/WindowUtils';

import './style.scss';

export interface IMultiLevelDropdownProps {
  id: string;
  name: string;
  label: string;
  value: any;
  options: IMultiLevelOptions[];
  onChange: TButtonOnClick;
  onBlur?: TButtonOnClick;
  onFocus?: TButtonOnClick;
  onSubmit?: (e: React.SyntheticEvent<any>, handleClose?: boolean) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: IFieldValidation;
  width?: string;
  shouldHighlightMatchingSubstring?: boolean;
  searchMode?: boolean;
  offset?: number;
  searchPlaceholder?: string;
  onLoadMoreData?: (params: ILoadMoreParams) => void;
  lazyLoad?: boolean;
  filters?: ICaseFilters;
  shouldFetchItems?: boolean;
}

interface IData {
  query: string;
  count: number;
}

interface IMultiLevelDropdownState {
  show: boolean;
  displayOptions: boolean[];
  dropdownMetaData: Record<string, IData>;
  offset: number; // used in case offset is not provided in props
}

export class MultiLevelDropdown extends React.Component<IMultiLevelDropdownProps, IMultiLevelDropdownState> {
  private lcfDateFilterRefs: { [key: string]: DateFilter } = {};

  public static defaultProps = {
    name: '',
    value: '',
    label: '',
    clearable: false,
    onChange: null,
    placeholder: 'Select Option',
    options: null,
    disabled: false,
    shouldHighlightMatchingSubstring: true,
    searchMode: false,
    shouldFetchItems: true,
  };

  constructor(props: IMultiLevelDropdownProps) {
    super(props);
    this.state = this.getInitState();
  }

  getInitState = () => ({
    show: false,
    displayOptions: this.props.options.map((option) => false),
    dropdownMetaData: {},
    offset: 0,
  });

  toggleDropdown = (e?: React.SyntheticEvent<any>) => {
    this.setState((prevState) => ({
      show: !this.props.disabled && !prevState.show,
    }));
  };

  onSearchQueryChange = (e: React.SyntheticEvent<any>, id: number, count: number) => {
    const query = e.currentTarget.value;

    this.setState(
      (prevState) => ({
        dropdownMetaData: {
          ...prevState.dropdownMetaData,
          [id]: { query, count },
        },
      }),
      () => {
        if (this.props.lazyLoad) {
          const limit = 20;
          const offset = 0;
          this.props.onLoadMoreData({
            query: query.trim(),
            type: this.props.id,
            limit,
            offset,
            id,
            count,
          });
        }
      }
    );
  };

  onClearSearchQuery = (id: number) => {
    this.setState(
      (prevState) => ({
        dropdownMetaData: {
          ...prevState.dropdownMetaData,
          [id]: {
            query: '',
            count: prevState.dropdownMetaData[id] ? prevState.dropdownMetaData[id].count || 0 : 0,
          },
        },
      }),
      () => {
        if (this.props.lazyLoad) {
          const limit = 20;
          const offset = 0;
          this.props.onLoadMoreData({
            query: '',
            type: this.props.id,
            limit,
            offset,
            id,
          });
        }
      }
    );
  };

  handleClickOutside = () => {
    this.setState(this.getInitState());
  };

  /**
   * Child values of a top-level option, or null when the option is childless.
   * The distinction matters: a childless option (e.g. "No closure detail") is
   * directly selectable, whereas a group that defines an empty value list has
   * nothing to select and must not be selectable at all.
   */
  getChildValues = (index: number) => this.props.options[index]?.options?.values ?? null;

  isEmptyGroup = (index: number) => {
    const children = this.getChildValues(index);

    return !!children && !children.length;
  };

  // A childless top-level option has no peers to satisfy; treat as vacuously true.
  allPeersSelected = (valueList: string[], levels: number[]) =>
    (this.getChildValues(levels[0]) ?? []).every((elem) => valueList.indexOf(elem.value) > -1);

  allChildrenSelected = (valueList: string[], index: number) => {
    const children = this.getChildValues(index);

    // Childless option: vacuously true, so a checked childless parent reads as fully selected.
    if (!children) {
      return true;
    }

    // A group defining no values can never be selected, so it never reads as checked.
    if (!children.length) {
      return false;
    }

    return children.every((elem) => valueList.indexOf(elem.value) > -1);
  };

  // No children -> [].some() === false, so a childless parent never renders indeterminate.
  atleastOneChildSelected = (valueList: string[], index: number) =>
    (this.getChildValues(index) ?? []).some((elem) => valueList.includes(elem.value));

  changeSecondLevel = (levels: number[]) => {
    const { options, value } = this.props;
    const dataPoint = options[levels[0]].options.values[levels[1]].value;

    let updatedValue = !value.includes(dataPoint)
      ? value.concat(dataPoint)
      : value.filter((elem) => elem !== dataPoint && elem !== options[levels[0]].value);

    if (this.allPeersSelected(updatedValue, levels)) {
      updatedValue = !updatedValue.includes(options[levels[0]].value)
        ? updatedValue.concat(options[levels[0]].value)
        : updatedValue.filter((elem) => elem !== options[levels[0]].value);
    } else {
      updatedValue = updatedValue.filter((elem) => elem !== options[levels[0]].value);
    }

    return updatedValue;
  };

  toggleParentSelector = (index: number, levels: number[]) => {
    const { options, value } = this.props;

    // A group defining no values would contribute only a bare group token, which
    // consumers decoding child values ignore. Leave the selection untouched.
    if (this.isEmptyGroup(index)) {
      return value;
    }

    const dataPoint = options[index] ? options[index].value : '';
    // A childless top-level option (e.g. "No closure detail") toggles only its own value.
    const childValues = this.getChildValues(levels[0]) ?? [];
    let updatedValue: string[];
    if (!value.includes(dataPoint)) {
      const childToAdd = childValues
        .filter((childElem) => !value.find((elem) => elem === childElem.value))
        .map((elem) => elem.value);

      updatedValue = value.concat(dataPoint).concat(childToAdd);
    } else {
      updatedValue = value.filter((elem) => {
        const foundChild = !childValues.find((childElem) => elem === childElem.value);
        const checkChild = elem !== dataPoint && foundChild;

        return checkChild;
      });
    }
    if (updatedValue.includes(dataPoint) && !this.state.displayOptions[index]) {
      this.toggleDropdownList(index);
    }

    return updatedValue;
  };

  onLcfDateChange = (index: number, isChecked: boolean) => (e: React.SyntheticEvent<any>) => {
    const childRef = this.lcfDateFilterRefs[index];
    const shouldToggleDropdown =
      (this.state.displayOptions[index] && isChecked) || (!isChecked && !this.state.displayOptions[index]);

    shouldToggleDropdown && this.toggleDropdownList(index);
    isChecked ? childRef?.onClose(e) : childRef?.handleOnSubmit(e);
  };

  onChange = (index: number, levels: number[]) => (e: React.SyntheticEvent<any>) => {
    const updatedValue = levels.length > 1 ? this.changeSecondLevel(levels) : this.toggleParentSelector(index, levels);

    const updatedEventObj = {
      name: this.props.name,
      value: updatedValue,
    };

    this.props.onChange({
      target: updatedEventObj,
      currentTarget: updatedEventObj,
    });
  };

  renderSelector = () => {
    const { options, value, label, placeholder, id, disabled } = this.props;
    // Fragments are collected then joined: concatenating them ran selections together, so a
    // childless option picked alongside a group read as "DuplicateNo closure detail".
    const filterLabel = options
      .reduce((acc: string[], curr) => {
        // A childless top-level option contributes just its own label when selected.
        if (!curr.options) {
          return value.includes(curr.value) ? [...acc, curr.label] : acc;
        }

        const selected = curr.options.values.filter((elem) => value.includes(elem.value));
        if (!selected.length) {
          return acc;
        }

        // A fully selected group collapses to the group label alone.
        return selected.length === curr.options.values.length
          ? [...acc, curr.label]
          : [...acc, `${curr.label}: ${selected.map((elem) => elem.label).join(', ')}`];
      }, [])
      .join(', ')
      .replace('Open', ' Open ');

    return (
      <div className={classnames('dropdown__selector', { disabled })} onClick={this.toggleDropdown}>
        {label && (
          <label htmlFor={id} className={classnames('dropdown__selector__label')}>
            {label}
          </label>
        )}
        <div className={classnames('dropdown__selector__selected')}>
          <label>{filterLabel || placeholder}</label>
          <span className={classnames('dropdown__arrow')} />
        </div>
      </div>
    );
  };

  renderDropdown = () => {
    const { options, id } = this.props;

    return (
      <div id={id} className={classnames('dropdown__list', 'nested__list')}>
        {options && (
          <ul
            className={classnames('dropdown__options', {
              searchMode: this.props.searchMode,
            })}
          >
            {options.map((option, index) => this.renderList(option, index, [index], option.disabled))}
          </ul>
        )}
      </div>
    );
  };

  handleContainerOnBottom = (e: any, id: number, optionsLength: number, count: number) => {
    if (this.props.lazyLoad && this.props.shouldFetchItems) {
      const element = e.target;
      const offsetheight = 40;
      const limit = 20;
      if (element.scrollHeight - element.scrollTop <= element.clientHeight + offsetheight) {
        this.setState((prevState) => {
          const newOffset = prevState.offset + limit;
          this.props.onLoadMoreData({
            offset: this.props.offset ? this.props.offset : newOffset,
            type: this.props.id,
            limit,
            query: '',
            id,
            count,
          });

          return { offset: newOffset };
        });
      }
    }
  };

  toggleDropdownList(index: number) {
    this.setState((prevState) => ({
      ...prevState,
      displayOptions: [
        ...prevState.displayOptions.slice(0, index),
        !prevState.displayOptions[index],
        ...prevState.displayOptions.slice(index + 1),
      ],
    }));
  }

  getDateFromString(createdAt: string) {
    const arr = createdAt.split('-');
    if (arr.length >= 4) {
      return arr.slice(0, 2).join('-');
    }

    return arr[0];
  }

  getSearchInputByType = (type: CustomFieldType, id: number, index: number, disabled = false) => {
    const { dropdownMetaData } = this.state;
    const query = dropdownMetaData[id] ? dropdownMetaData[id].query : '';
    const count = dropdownMetaData[id] ? dropdownMetaData[id].count : 0;

    if (type === CustomFieldType.DATE) {
      // tslint:disable-next-line: max-line-length
      const defaultDateRange = `${getTodaysDate()}-${getTodaysDate()}-${id}-${type}`;
      const createdAt = this.props.filters && this.props.filters[id] ? this.props.filters[id] : '';
      const createdOnFilterOptions = [
        { value: `${DateFilterType.ANYTIME}-${id}-${type}`, label: 'Any Time' },
        { value: `${DateFilterType.TODAY}-${id}-${type}`, label: 'Today' },
        {
          value: `${DateFilterType.LAST_SEVEN_DAYS}-${id}-${type}`,
          label: 'Last 7 Days',
        },
        {
          value: `${DateFilterType.LAST_THIRTY_DAYS}-${id}-${type}`,
          label: 'Last 30 Days',
        },
        {
          value: createdAt && !isValidDateFilter(this.getDateFromString(createdAt)) ? createdAt : defaultDateRange,
          label: 'Custom',
        },
      ];

      return (
        <DateFilter
          key="createdAt"
          label="Date"
          name="locationCustomFieldValues"
          className={classnames('date-filter')}
          options={createdOnFilterOptions}
          selectedOption={createdAt || DateFilterType.ANYTIME}
          onSubmit={this.props.onSubmit}
          isFilterOpen={true}
          disabled={disabled}
          onCloseOption={createdOnFilterOptions[0]}
          ref={(ref) => (this.lcfDateFilterRefs[index] = ref)}
        />
      );
    } else if (type === CustomFieldType.PICKLIST) {
      return null;
    } else {
      return (
        <div
          className={classnames('search', {
            'search--searching': query.trim(),
          })}
        >
          <Input
            field={{
              label: '',
              type: CustomFieldType.TEXT,
              isRequired: false,
              value: this.state.dropdownMetaData[id] ? this.state.dropdownMetaData[id].query : '',
            }}
            placeholder={this.props.searchPlaceholder}
            width={12}
            name="query"
            onChange={(e) => this.onSearchQueryChange(e, id, count)}
            className={classnames('search__input')}
            disabled={disabled}
          />
          <span className={classnames('search__img')} onClick={() => this.onClearSearchQuery(id)} />
        </div>
      );
    }
  };

  showSearchInput = (option: IMultiLevelOptions) => {
    if (option.type === CustomFieldType.DATE || option.type === CustomFieldType.PICKLIST) {
      return this.props.searchMode;
    }

    return this.props.searchMode && !!option.totalCount;
  };

  renderList(option: IMultiLevelOptions, index: number, level: number[], disabled = false) {
    const { value, id } = this.props;
    let isChecked;

    const isSelected = (optionPoint: { value: any }) =>
      value ? value.find((e: any) => e === optionPoint.value) : false;

    const isLocationCustomFieldFilter = this.props.name === 'locationCustomFields';

    if (isLocationCustomFieldFilter && option.type === CustomFieldType.DATE) {
      const label = `${option.id}-DATE`;
      isChecked = value ? value.some((lcf) => lcf.includes(label)) : false;
    } else if (level.length > 1) {
      isChecked = value ? value.includes(option.value) : false;
    } else {
      isChecked = value ? (value.includes(option.value) ? this.allChildrenSelected(value, index) : false) : false;
    }

    const showCheckbox = isLocationCustomFieldFilter || (option.type ? option.type === CustomFieldType.PICKLIST : true);
    const onChange =
      isLocationCustomFieldFilter && option.type === CustomFieldType.DATE
        ? this.onLcfDateChange(index, isChecked)
        : this.onChange(index, level);

    return (
      <li
        className={classnames({
          selected: isSelected(option),
          selectable: !option.options,
        })}
        key={index}
      >
        <span className={classnames('parent__list')}>
          {showCheckbox ? (
            <Checkbox
              id={`${id}-checkbox-${level.join('-')}`}
              onChange={onChange}
              isChecked={isChecked}
              name={option.label}
              isIntermediate={
                level.length === 1
                  ? !this.allChildrenSelected(value, index) && this.atleastOneChildSelected(value, index)
                  : false
              }
              className="checkbox-holder--radio"
              disabled={disabled || (level.length === 1 && this.isEmptyGroup(index))}
            >
              <span
                className={classnames({
                  heading: option.options,
                })}
              >
                <ClippedText text={option.label || ''} clipAfter={50} />
              </span>
            </Checkbox>
          ) : (
            <span className={classnames('parent__list__label')}>{option.label}</span>
          )}
          <span
            className={classnames('fa-solid fa-plus', 'toggle__child', {
              hide: level.length > 1 || !option.options ? true : this.state.displayOptions[index],
            })}
            onClick={(e) => this.toggleDropdownList(index)}
          />
          <span
            className={classnames('fa-solid fa-minus', 'toggle__child', {
              hide: level.length > 1 || !option.options ? true : !this.state.displayOptions[index],
            })}
            onClick={(e) => this.toggleDropdownList(index)}
          />
        </span>
        {option.options && (
          <ul
            onScroll={(e) =>
              option.type !== CustomFieldType.DATE &&
              option.type !== CustomFieldType.PICKLIST &&
              this.handleContainerOnBottom(e, option.id, option.options.values.length, option.count)
            }
            id={`${id}-${level.join('-')}`}
            className={classnames('dropdown__options', 'nested__options', {
              hide: !this.state.displayOptions[index],
              searchMode: this.props.searchMode,
            })}
          >
            {option.options.heading && (
              <label className={classnames('dropdown__selector__label', 'nested__selector__label')}>
                {option.options.heading}
              </label>
            )}
            {this.showSearchInput(option) && this.getSearchInputByType(option.type, option.id, index, disabled)}
            {option.options.values.map((nestedOption, nestedIndex) =>
              this.renderList(nestedOption, nestedIndex, level.concat(nestedIndex), disabled)
            )}
          </ul>
        )}
      </li>
    );
  }

  render() {
    const { width } = this.props;
    const boundingClientRect = getBoundingClientRect('.dropdown__wrapper');
    const style = boundingClientRect
      ? {
          maxWidth: width || boundingClientRect.width,
        }
      : null;

    return (
      <div
        style={style}
        className={classnames('dropdown__wrapper', {
          'dropdown__wrapper--open': this.state.show,
        })}
      >
        {this.renderSelector()}
        {this.state.show && this.renderDropdown()}
      </div>
    );
  }
}

export default onClickOutside(MultiLevelDropdown);
